// Copyright (c) 2026 Michael F. Kamprath
//
// alpha-mask.js -- derive a sampleable alpha-channel mask from an
// image URL.
//
// Used by cloud-spawner.js's pixel-perfect hit testing:
//
//   - Cloud mask: reject taps that fall on 100%-transparent parts of
//     the cloud sprite's texture (the rectangular bounding box
//     includes sky pixels outside the cloud's visible silhouette).
//   - Backdrop mask: reject taps that fall on opaque parts of the
//     theme's backdrop (the barn, trees, hills) -- visually the
//     cloud is hidden behind the backdrop at those points, so a tap
//     there really targets the backdrop, not the cloud.
//
// Implementation: loads the image from a URL via a fresh
// HTMLImageElement (rather than sampling PIXI's existing texture
// resource, which PIXI v8 may release after uploading to WebGL,
// leaving an undrawable ImageBitmap behind), then draws it to an
// offscreen 2D canvas.  `getAlphaAt(u, v)` reads the alpha channel
// at normalized texture coordinates in [0, 1].
//
// The offscreen canvas may optionally be downsampled to `maxDim` to
// bound ImageData memory on large backdrops (1024+ px wide).  At
// maxDim=512 a 1024x768 backdrop sits at ~400 kB of alpha-bytes.

const DEFAULT_MAX_DIM = 512;

/**
 * @typedef {Object} AlphaMask
 * @property {number} width     Sampled width in pixels.
 * @property {number} height    Sampled height in pixels.
 * @property {(u: number, v: number) => number} getAlphaAt
 *   Returns alpha 0..255 at the normalized (u, v) texture coord.
 *   Returns 0 for out-of-range inputs.
 */

/**
 * Load an image by URL and build an alpha mask from it.  The Image
 * element is same-origin (all theme images are served from /public)
 * so the canvas isn't tainted.  Returns null on load failure or
 * when the canvas can't be sampled (jsdom, unusual environments).
 *
 * @param {string} url
 * @param {{ maxDim?: number }} [opts]
 * @returns {Promise<AlphaMask | null>}
 */
export function createAlphaMaskFromUrl(url, opts = {}) {
  if (!url || typeof document === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const mask = maskFromDrawable(img, img.naturalWidth, img.naturalHeight, opts);
      resolve(mask);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * Build a mask from any drawable source (HTMLImageElement,
 * ImageBitmap, HTMLCanvasElement) whose natural dimensions are
 * known.  Broken out from createAlphaMaskFromUrl so tests can feed
 * it a pre-baked canvas and sidestep the async image load.
 *
 * @param {CanvasImageSource} source
 * @param {number} texW
 * @param {number} texH
 * @param {{ maxDim?: number }} [opts]
 * @returns {AlphaMask | null}
 */
export function maskFromDrawable(source, texW, texH, opts = {}) {
  if (!source || !texW || !texH) return null;

  const maxDim = opts.maxDim || DEFAULT_MAX_DIM;
  const scale = Math.min(1, maxDim / Math.max(texW, texH));
  const w = Math.max(1, Math.floor(texW * scale));
  const h = Math.max(1, Math.floor(texH * scale));

  let data = null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Leave imageSmoothingEnabled at its default (true) so the
    // downsample uses bilinear blending.  Nearest-neighbour would
    // pick every Nth pixel on the source grid, which can alias
    // through feathered cloud edges and leave holes in the mask.
    ctx.drawImage(source, 0, 0, w, h);
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null;
  }

  return {
    width: w,
    height: h,
    getAlphaAt(u, v) {
      if (!(u >= 0) || u > 1 || !(v >= 0) || v > 1) return 0;
      const x = Math.min(w - 1, Math.floor(u * w));
      const y = Math.min(h - 1, Math.floor(v * h));
      return data[(y * w + x) * 4 + 3] ?? 0;
    },
  };
}
